import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EditModalFooter from './EditModalFooter'
import {
  PatchBusItemViewModel,
  PatchFerryItemViewModel,
  PatchOtherTransportItemViewModel,
  PatchTrainItemViewModel,
} from './models'
import {
  extractEditableEndDateTimeStrings,
  extractEditableStartDateTimeStrings,
  updateBusPayloadFromViewModel,
  updateFerryPayloadFromViewModel,
  updateOtherTransportPayloadFromViewModel,
  updateTrainPayloadFromViewModel,
} from './utils'
import BusFields from '../Common/BusFields'
import FerryFields from '../Common/FerryFields'
import { formOptions } from '../Common/Fields/config'
import OtherTransportFields from '../Common/OtherTransportFields'
import TrainFields from '../Common/TrainFields'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { TransportItem } from 'tripPlanner/types/tripItem'

type PatchTransportItemViewModel =
  | PatchTrainItemViewModel
  | PatchBusItemViewModel
  | PatchFerryItemViewModel
  | PatchOtherTransportItemViewModel

type ItemTypes = TransportItem

interface Props {
  item: ItemTypes
}

const itemToView = (item: ItemTypes): PatchTransportItemViewModel => {
  return {
    type: item.type,
    price: item.price,
    url: item.url,
    isBooked: item.isBooked,
    notes: item.notes,
    operatorName: item.operatorName,
    confirmationCode: item.confirmationCode,
    seat: item.seat,
    ...extractEditableStartDateTimeStrings(item),
    ...extractEditableEndDateTimeStrings(item),
    startPlace: item.startPlace,
    endPlace: item.endPlace,
    isEndDateSame:
      !!item.startDate && !!item.endDate && item.startDate.isSame(item.endDate),
  }
}

function EditTransport(props: Props) {
  const { item } = props

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchTransportItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })
  const { handleSubmit } = methods

  const { mutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = () =>
    openModal(DeleteTripItem, { item }, { height: 'auto' })

  const updateItem = useCallback(
    (viewModel: PatchTransportItemViewModel) => {
      switch (viewModel.type) {
        case 'TRAIN':
          mutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateTrainPayloadFromViewModel(viewModel),
          })
          break
        case 'FERRY':
          mutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateFerryPayloadFromViewModel(viewModel),
          })
          break
        case 'OTHER_TRANSPORT':
          mutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateOtherTransportPayloadFromViewModel(viewModel),
          })
          break
        case 'BUS':
          mutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: updateBusPayloadFromViewModel(viewModel),
          })
          break
      }
    },
    [item, mutate],
  )

  return (
    <>
      <ModalHeader title="Edit transport" onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            {item.type === 'TRAIN' && <TrainFields tripId={item.tripId} />}
            {item.type === 'FERRY' && <FerryFields tripId={item.tripId} />}
            {item.type === 'BUS' && <BusFields tripId={item.tripId} />}
            {item.type === 'OTHER_TRANSPORT' && (
              <OtherTransportFields tripId={item.tripId} />
            )}
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditTransport
